/**
 * ScoreDrillDown.tsx — Shared 4-section metrics drill-down content component.
 *
 * Used by InfoSidePanel (desktop) and vaul Drawer (mobile). Pure content — no
 * scroll or positional CSS; the parent container owns the scroll surface.
 *
 * Sections:
 *   1. Scores    — CS/RDS/HCC/HCO/DTS (0–1 decimal 2dp) + PPI/CDPI/CMPI/Gap percentiles
 *   2. Context   — population, density, income, EV reg/1k, dominant type
 *   3. All metrics — INPUT_GROUPS as Accordion; dominant group open by default
 *   4. Site details — EV competition + amenities (hidden when no siteScore)
 *
 * Color coding (D-12 thresholds via scoreColor — one coding language across the whole drawer):
 *   - Section 1 sub-scores CS/RDS/HCC/DTS: scoreColor(value * 100)  [0–1 → 0–100]
 *   - Section 1 HCO: hcoColor(value * 100) — INVERTED (high HCO = home charging, bad for public)
 *   - Section 1 percentile rows: scoreColor(pctile)                     [0–100 rank]
 *   - Section 3 "All metrics": direction-aware scoreColor(favorableShare(v)) ONLY for metrics
 *     flagged colorBasis 'share100' (full-range 0–100% demographic proportions). Everything else
 *     — absolute magnitudes, job shares, confounded port shares — stays NEUTRAL (favorableShare
 *     → null): we never invent a threshold for a value with no defensible fixed banding. (T-166)
 *   - Section 2 "Context": neutral by design (population/density/income/EV-reg are magnitudes
 *     with no in-app reference distribution to band against).
 *   - All value text defaults to the `text-foreground` token so it is legible in both themes.
 */
import { useMemo, useState } from 'react'
import { ChevronDown, Info } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from '@/components/ui/collapsible'
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from '@/components/ui/accordion'
import { SCORE_DEFS } from '@/lib/scoreDefinitions'
import {
  INPUT_GROUPS,
  formatValue,
  pctileLabel,
  dominantTypeToGroup,
  favorableShare,
  levelForTractField,
} from '@/lib/siteAnalysis'
import type { CensusTract, SiteScoreResult } from '@/lib/tractModel'
import { dominantTypeLabel, SCORE_LABELS } from '@/lib/tractModel'
import { canonicalNearbyAmenities } from '@/lib/nearbyStats'
import { MetricInfoButton } from './MetricInfoButton'
import { GeoLevelTag } from './GeoLevelTag'
import type { GeoLevel } from '@/lib/geoLevel'
import { SiteClassificationSection } from './SiteClassificationSection'
import {
  selectLocationClassification,
  siteSpecificRows,
  type LocationClassification,
} from '@/lib/locationClassification'
import { useSiteVicinity } from '@/hooks/useSiteVicinity'
import { NearbyChargersSection } from './NearbyChargersSection'

// ---- Exported types --------------------------------------------------------

export interface ScoreDrillDownProps {
  tract: CensusTract
  siteScore?: SiteScoreResult | null
  /** The searched site (pin) lat/lng — drives the T-512 block/block-group classification readout.
   *  Null when there is no site pin, in which case that readout renders nothing. */
  site?: { lat: number; lng: number } | null
  /** T-628: the classification resolved upstream; when omitted the section fetches for itself. */
  siteClassification?: LocationClassification | null
}

// ---- Color helpers ---------------------------------------------------------

// D-12: verbatim thresholds — do not change (same as ScorePanel.tsx)
export function scoreColor(value: number): string {
  if (value >= 70) return 'var(--score-high)'
  if (value >= 60) return 'var(--score-good)'
  if (value >= 45) return 'var(--score-moderate)'
  if (value >= 35) return 'var(--score-low)'
  return 'var(--score-poor)'
}

// T-497: the default F-term POI radius (miles) the "Nearby amenities" counts are gathered at.
// Mirrors apps/api scoringConfigRuntime DEFAULT_SITE_SCORE_TIERS.radii.poiMi. Used ONLY as a
// fallback when a (cached/older) site-score response predates the `amenities.poiRadiusMi` field —
// a live response carries the actual radius, so the footnote states the real query radius.
const DEFAULT_AMENITY_RADIUS_MI = 0.25

// T-654: the supplemental all-category gather's result ceiling (ALS: 100). Fallback only, for a
// cached response predating `amenities.poiResultCap`; a live response carries the actual cap.
const DEFAULT_NEARBY_POI_RESULT_CAP = 100

// D-12 tier words — verbatim mirror of ScorePanel's TIER_WORDS. Duplicated (not imported) for the
// same reason scoreColor above is: ScorePanel already imports cmpiColor from here, so importing back
// would make a cycle out of two tiny, threshold-frozen helpers. Keep in lockstep with ScorePanel.
function tierWord(score: number): string {
  if (score >= 70) return 'Prime Opportunity'
  if (score >= 60) return 'Strong'
  if (score >= 45) return 'Moderate'
  if (score >= 35) return 'Marginal'
  return 'Unfavorable'
}

/**
 * CMPI (Market Pressure Index) is the ONE inverse index in the model: a HIGHER value means a
 * MORE crowded market for a new entrant (scoreDefinitions higherMeans). So a low value — 0 means
 * "no existing stations", an open market — is GOOD for a site operator and must read green, while
 * a saturated market must read red: the opposite of every other (higher-is-better) index. Band it
 * on the inverted D-12 scale. Only the color inverts; the displayed number is unchanged. (T-199)
 */
export function cmpiColor(value: number): string {
  return scoreColor(100 - value)
}

/**
 * HCO (Home-Charging Optimized): high HCO means residents charge at home and have less need
 * for public chargers — bad for site investment. Invert so high HCO reads red, low reads green.
 */
export function hcoColor(value: number): string {
  return scoreColor(100 - value)
}

// ---- Local sub-components --------------------------------------------------

interface CollapsibleSectionProps {
  title: string
  children: React.ReactNode
  defaultOpen?: boolean
}

function CollapsibleSection({ title, children, defaultOpen = true }: CollapsibleSectionProps) {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger asChild>
        <button
          className="flex w-full items-center justify-between px-3 py-2
                     text-sm font-bold text-foreground hover:bg-muted/30
                     min-h-[44px] border-b border-border"
        >
          {title}
          <ChevronDown className={cn('h-4 w-4 transition-transform', open && 'rotate-180')} />
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="px-3 py-2">{children}</div>
      </CollapsibleContent>
    </Collapsible>
  )
}

interface MetricRowProps {
  label: string
  value: string
  metricKey?: string
  /** Optional inline color for the value span (Section 1 score color coding). */
  color?: string
  /**
   * T-537: geographic level of this metric. Optional so rows that are not pipeline metrics
   * (the Context section's location type, for instance) are not forced to claim one.
   */
  level?: GeoLevel
}

function MetricRow({ label, value, metricKey, color, level }: MetricRowProps) {
  return (
    <div className="flex items-center justify-between py-1 border-b border-border/40 last:border-0">
      <div className="flex items-center gap-0.5 text-sm text-secondary-foreground">
        {label}
        {metricKey && <MetricInfoButton metricKey={metricKey} label={label} />}
        {level && <GeoLevelTag level={level} metricLabel={label} />}
      </div>
      {/* T-166: default to the `text-foreground` theme token so a value with no direction-aware
          color is still legible in BOTH themes (never the inherited browser-default black). An
          explicit `color` (score/share coding) wins via the inline style. */}
      <span
        className="text-sm font-bold tabular-nums text-foreground"
        style={color ? { color } : undefined}
      >
        {value}
      </span>
    </div>
  )
}

// ---- Main component --------------------------------------------------------

export function ScoreDrillDown({
  tract,
  siteScore,
  site = null,
  siteClassification: externalClassification,
}: ScoreDrillDownProps) {
  // Fetch vicinity data locally as fallback when the parent doesn't provide classification
  const { vicinity } = useSiteVicinity(externalClassification === undefined ? site : null)
  const localClassification = useMemo(
    () => (site && vicinity ? selectLocationClassification(site, vicinity) : null),
    [site, vicinity],
  )
  const siteClassification = externalClassification ?? localClassification

  // Dominant group key used as defaultValue for the Accordion in "All metrics"
  // INPUT_GROUPS is a const array with 5 elements — [0] is always defined
  const dominantKey = dominantTypeToGroup(tract.dominantType) ?? INPUT_GROUPS[0]!.key

  // T-497 (Noreen item 3): the headline Opportunity Score for this view. Same rule as the compact
  // ScorePanel — the site composite when a site score is present, otherwise the tract LVI.
  const displayScore = siteScore ? siteScore.opportunityScore : tract.lviScore

  // T-654: the amenity figure is the ONE canonical count both surfaces render — the SAME categories
  // and counts the PDF one-pager shows, computed from the SAME payload nearby-POI set (re-bucketed to
  // the 0.25 mi F-term radius by canonicalNearbyAmenities), so screen and PDF cannot contradict.
  // T-644 still applies: the supplemental gather is nearest-first with a cap, so when it fills, every
  // count is a floor — marked (N+) and disclosed, never printed as an exact total.
  const amenities = siteScore?.amenities
  const canonicalAmenities =
    amenities?.nearbyPois && amenities.nearbyPois.length > 0
      ? canonicalNearbyAmenities(amenities.nearbyPois, {
          radiusMi: amenities.poiRadiusMi ?? DEFAULT_AMENITY_RADIUS_MI,
          poiFetched: amenities.poiFetched ?? 0,
          poiResultCap: amenities.poiResultCap ?? DEFAULT_NEARBY_POI_RESULT_CAP,
        })
      : null

  return (
    <div>
      {/* ── T-497: prominent Opportunity Score hero ─────────────────────
          In view-all-metrics the score used to show only as a small number in the panel header.
          Lead with it here (big number + tier word) so it is unmissable — the drill-down is shared
          by the desktop InfoSidePanel and the mobile sheet, so this covers both. */}
      <div className="border-b border-border px-3 pt-3 pb-2.5">
        <div className="text-xs font-medium text-muted-foreground">{SCORE_LABELS.lvi}</div>
        <div className="mt-0.5 flex items-baseline gap-2">
          <span
            data-testid="drilldown-opportunity-score"
            style={{
              fontSize: '2.75rem',
              fontWeight: 700,
              lineHeight: 1,
              color: scoreColor(displayScore),
            }}
          >
            {Math.round(displayScore)}
          </span>
          <span className="text-sm font-semibold" style={{ color: scoreColor(displayScore) }}>
            {tierWord(displayScore)}
          </span>
        </div>
      </div>

      {/* ── T-352: degraded truth-in-labeling banner ───────────────────
          When the tract `sam` baseline is unavailable the API cannot recompute a
          site-specific LVI and returns the tract LVI (v2.degraded=true) — the current
          prod state for every tract until the sam publish (T-335). Say so plainly: the
          headline is tract-level, the site-specific LVI is pending data, and the
          site-gathered factors (competition + amenities in "Site details") are what
          the site analysis produced. `text-muted-foreground` reads in both themes. */}
      {siteScore?.v2?.degraded && (
        <div className="mx-3 mt-3 flex items-start gap-2 rounded-md bg-muted/50 px-2.5 py-2 text-xs text-muted-foreground">
          <Info size={14} aria-hidden="true" className="mt-0.5 flex-shrink-0" />
          <p>
            Site-specific LVI pending data — the score shown is this location's{' '}
            <span className="font-semibold text-foreground">tract-level LVI</span>. Nearby
            competition and amenities in Site details reflect the site analysis.
          </p>
        </div>
      )}

      {/* ── T-498: location classification — a first-class element near the top ───────────
          Noreen asked for the corridor/retail/residential classification (T-512) to be PROMINENT,
          not a gray row buried in "Site details" (its former home). It sits here, directly under
          the score hero, as a headline attribute of the site: the score says how good the site is;
          this says what KIND of place it is. Site-driven (it needs the searched pin, not a full
          site score), so it renders nothing without a pin. It composes with the T-497 score hero
          above it — the two never compete. */}
      <div className="px-3">
        <SiteClassificationSection site={site} classification={siteClassification} />
      </div>

      {/* ── Section 1: Sub-Scores ──────────────────────────────────────── */}
      <CollapsibleSection title="Sub-Scores" defaultOpen>
        {/* CDPI, CMPI, Gap: site-level when available, tract fallback.
            PPI: always tract-level (does not vary within a tract). */}
        <MetricRow
          label={SCORE_DEFS['CDPI'].shortLabel}
          value={
            siteScore?.v2?.siteCdpi != null
              ? `${Math.round(siteScore.v2.siteCdpi)}`
              : pctileLabel(tract.cdpiPctileState ?? null)
          }
          metricKey="CDPI"
          level={siteScore?.v2?.siteCdpi != null ? 'site' : SCORE_DEFS['CDPI'].level}
          color={
            siteScore?.v2?.siteCdpi != null
              ? scoreColor(siteScore.v2.siteCdpi)
              : tract.cdpiPctileState != null
                ? scoreColor(tract.cdpiPctileState)
                : undefined
          }
        />
        <MetricRow
          label={SCORE_DEFS['CMPI'].shortLabel}
          value={
            siteScore?.v2?.siteCmpi != null
              ? `${Math.round(siteScore.v2.siteCmpi)}`
              : pctileLabel(tract.cmpiPctileState ?? null)
          }
          metricKey="CMPI"
          level={siteScore?.v2?.siteCmpi != null ? 'site' : SCORE_DEFS['CMPI'].level}
          color={
            siteScore?.v2?.siteCmpi != null
              ? cmpiColor(siteScore.v2.siteCmpi)
              : tract.cmpiPctileState != null
                ? cmpiColor(tract.cmpiPctileState)
                : undefined
          }
        />
        <MetricRow
          label={SCORE_DEFS['Gap'].shortLabel}
          value={
            siteScore?.v2?.gapScore != null
              ? `${Math.round(siteScore.v2.gapScore)}`
              : pctileLabel(tract.gapPctileState ?? null)
          }
          metricKey="Gap"
          level={siteScore?.v2?.gapScore != null ? 'site' : SCORE_DEFS['Gap'].level}
          color={
            siteScore?.v2?.gapScore != null
              ? scoreColor(Math.max(0, Math.min(100, (siteScore.v2.gapScore + 100) / 2)))
              : tract.gapPctileState != null
                ? scoreColor(tract.gapPctileState)
                : undefined
          }
        />
        <MetricRow
          label={SCORE_DEFS['PPI'].shortLabel}
          value={pctileLabel(tract.ppiPctileState ?? null)}
          metricKey="PPI"
          level={'tract'}
          color={tract.ppiPctileState != null ? scoreColor(tract.ppiPctileState) : undefined}
        />
      </CollapsibleSection>

      {/* ── Section 1b: Location Scores ──────────────────────────────────── */}
      <CollapsibleSection title="Location Scores" defaultOpen>
        {/* Block / block-group scores (when available) */}
        {siteClassification && siteSpecificRows(siteClassification).length > 0 && (
          <>
            <h4 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mt-1 mb-1">
              Block / Block Group
            </h4>
            {siteSpecificRows(siteClassification).map((row) => (
              <MetricRow
                key={row.key}
                label={row.label}
                value={row.value.toFixed(1)}
                level={row.level}
                color={scoreColor(row.value)}
              />
            ))}
          </>
        )}
        {/* Tract-level location scores, displayed 0–100 */}
        <h4 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mt-2 mb-1">
          Tract
        </h4>
        {(['CS', 'RDS', 'DTS', 'HCC', 'HCO'] as const).map((key) => {
          const rawValue = tract[key] != null ? (tract[key] as number) : null
          return (
            <MetricRow
              key={key}
              label={SCORE_DEFS[key].shortLabel}
              value={rawValue != null ? Math.round(rawValue * 100).toString() : '—'}
              metricKey={key}
              color={
                rawValue != null
                  ? key === 'HCO'
                    ? hcoColor(rawValue * 100)
                    : scoreColor(rawValue * 100)
                  : undefined
              }
              level={'tract'}
            />
          )
        })}
      </CollapsibleSection>

      {/* ── Section 2: Context ─────────────────────────────────────────── */}
      {/* T-537: these four repeat metrics that also appear in the All-metrics card below, so they
          take their level from the same mapping (levelForTractField) rather than a literal here —
          otherwise the same metric could show two different geographies in one panel. "EV reg. per
          1k" is precisely why that matters: it is a COUNTY figure, and shown as bare "context" it
          reads as a property of this tract. */}
      <CollapsibleSection title="Context" defaultOpen>
        <MetricRow
          label="Population"
          value={formatValue(tract.population ?? null, 'number')}
          level={levelForTractField('population') ?? undefined}
        />
        <MetricRow
          label="Pop. density"
          value={formatValue(tract.populationDensity ?? null, 'density')}
          level={levelForTractField('populationDensity') ?? undefined}
        />
        <MetricRow
          label="Median income"
          value={formatValue(tract.medianHhIncome ?? null, 'currency')}
          level={levelForTractField('medianHhIncome') ?? undefined}
        />
        <MetricRow
          label="EV reg. per 1k"
          value={formatValue(tract.evRegPer1000 ?? null, 'per1k')}
          level={levelForTractField('evRegPer1000') ?? undefined}
        />
        {/* Tract-level location type: humanized dominant_type (never the raw enum token) */}
        <MetricRow label="Tract location type" value={dominantTypeLabel(tract.dominantType)} />
      </CollapsibleSection>

      {/* ── Section 3: All metrics ─────────────────────────────────────── */}
      <CollapsibleSection title="All metrics" defaultOpen>
        <Accordion type="multiple" defaultValue={[dominantKey]}>
          {INPUT_GROUPS.map((group) => {
            const count = group.variables.length
            return (
              <AccordionItem key={group.key} value={group.key}>
                {/* T-166: `text-foreground` gives the group header readable contrast in BOTH
                    themes. Without it the trigger inherited the browser-default black, which was
                    dark-on-dark in the dark theme (the reported bug). */}
                <AccordionTrigger className="text-sm font-bold text-foreground">
                  <span>{group.title}</span>
                  <span className="text-xs text-muted-foreground font-normal ml-2">
                    {count} metrics
                  </span>
                </AccordionTrigger>
                <AccordionContent>
                  {group.variables.map((v) => {
                    const raw = (tract as unknown as Record<string, unknown>)[
                      v.tractField as string
                    ] as number | null
                    // T-166: direction-aware good/mid/bad only for full-range share metrics
                    // (favorableShare returns null otherwise → neutral, no invented threshold).
                    const fav = favorableShare(v, raw)
                    return (
                      <MetricRow
                        key={v.tractField as string}
                        label={v.label}
                        value={formatValue(raw, v.format)}
                        color={fav != null ? scoreColor(fav) : undefined}
                        // T-537: level comes from the data mapping, never from the component —
                        // the tag can only ever say what the pipeline provenance says.
                        level={v.level}
                      />
                    )
                  })}
                </AccordionContent>
              </AccordionItem>
            )
          })}
        </Accordion>
      </CollapsibleSection>

      {/* ── Section 4: Site details (hidden when no siteScore) ─────────── */}
      {siteScore && (
        <CollapsibleSection title="Site details" defaultOpen>
          {/* Raw inputs to D/F/M site adjustments */}
          {siteScore.v2 && (
            <>
              <MetricRow
                label="Distance to nearest ramp"
                value={
                  siteScore.v2.rampDistanceMi != null
                    ? `${siteScore.v2.rampDistanceMi.toFixed(2)} mi`
                    : '—'
                }
                level="site"
              />
              <MetricRow
                label={`POIs within ${siteScore.amenities?.poiRadiusMi ?? DEFAULT_AMENITY_RADIUS_MI} mi`}
                value={siteScore.v2.poiCount?.toString() ?? '—'}
                level="site"
              />
              <MetricRow
                label={`Competing stations within ${siteScore.v2.competition?.radiusMi ?? 0.5} mi`}
                value={
                  siteScore.evCompetition
                    ? `${siteScore.evCompetition.stationCount} (weighted: ${siteScore.v2.competition?.weighted.toFixed(1) ?? '—'})`
                    : '—'
                }
                level="site"
              />
              <MetricRow
                label="Distinct networks"
                value={siteScore.v2.competition?.distinctNetworks?.toString() ?? '—'}
                level="site"
              />
            </>
          )}
          {/* EV competition */}
          <div className="py-1 text-sm">
            <span className="text-secondary-foreground">EV charging stations nearby: </span>
            {/* T-216: the count value must carry the `text-foreground` theme token — like
                MetricRow — so it is legible in BOTH themes (it was inheriting browser-default
                black → dark-on-dark on the dark panel). */}
            <span className="font-bold text-foreground">
              {siteScore.evCompetition?.stationCount ?? 0}
            </span>
          </div>
          {/* Nearby amenities — T-654: the ONE canonical figure, identical to the PDF one-pager. */}
          {canonicalAmenities && canonicalAmenities.rows.length > 0 && (
            <div className="mt-2">
              <p className="text-xs text-muted-foreground mb-0.5">Nearby amenities:</p>
              {/* T-497 (Noreen item 9: "put what defines 'nearby' — in a circle or footnote").
                  State the actual radius the counts were gathered at, read from the response
                  (amenities.poiRadiusMi); a pre-field response falls back to the shipping default. */}
              <p
                data-testid="amenities-radius-note"
                className="text-[0.7rem] italic text-muted-foreground mb-1"
              >
                Within {canonicalAmenities.radiusMi} mi of the site
              </p>
              {canonicalAmenities.rows.map((r) => (
                <div key={r.category} className="flex justify-between text-sm py-0.5">
                  {/* T-654: the shared canonical label (Café→Coffee Shop, Parking→Parking Lots), the
                      SAME label the PDF prints, so the two surfaces read identically. */}
                  <span className="text-secondary-foreground">{r.label}</span>
                  {/* T-216: `text-foreground` theme token — same both-theme legibility fix.
                      T-644: a "+" when the fetch was capped, so a floor never reads as a fact. */}
                  <span className="font-bold text-foreground tabular-nums">
                    {canonicalAmenities.areFloor ? `${r.count}+` : r.count}
                  </span>
                </div>
              ))}
              {/* T-644: the counts above are the NEAREST poiResultCap results, so in a dense area
                  the true numbers can be several times higher. Say so where the counts are, not
                  only in the code — a floor shown as a fact is the defect this note closes. */}
              {canonicalAmenities.areFloor && (
                <p
                  data-testid="amenities-floor-note"
                  className="text-[0.7rem] italic text-muted-foreground mt-1"
                >
                  Counts marked “+” are at least the number shown — the nearby-place search stopped
                  at its closest {canonicalAmenities.poiResultCap} results, so a dense area can hold
                  several times as many.
                </p>
              )}
            </div>
          )}
          {/* T-497 (Noreen item 10): the richer nearby-chargers data the PDF one-pager prints —
              stations / DC-fast / ports at 0.5 & 1.0 mi — surfaced here too, from the same
              fetchNearbyStats source. Self-fetches from the site pin; renders nothing without one. */}
          <NearbyChargersSection site={site} siteScore={siteScore} />
        </CollapsibleSection>
      )}
    </div>
  )
}
